import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Car, Home, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { carBroker } from '@/lib/api';
import { useCurrency } from '@/lib/currency-context';
import { buildPathForLocale } from '@/lib/intl-seo';

export default function CarRentalBookingSuccessPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const { formatPrice } = useCurrency();
  const lc = (i18n.language || 'tr').slice(0, 2);
  const carsHome = buildPathForLocale(lc, ['car-rentals']);
  const bookingId = searchParams.get('id');
  const brokerPending = searchParams.get('broker_pending');
  const brokerError = searchParams.get('error');

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!bookingId) {
      setError(t('carRental.bookingNotFound', 'Rezervasyon bulunamadı'));
      setLoading(false);
      return;
    }
    carBroker
      .getBooking(bookingId)
      .then((res) => {
        if (res.data?.success) setBooking(res.data.booking);
        else setError(t('carRental.bookingNotFound', 'Rezervasyon bulunamadı'));
      })
      .catch(() => setError(t('carRental.bookingNotFound', 'Rezervasyon bulunamadı')))
      .finally(() => {
        sessionStorage.removeItem('pendingCarBrokerBooking');
        sessionStorage.removeItem('pendingCarBrokerBookingId');
        setLoading(false);
      });
  }, [bookingId, t]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-sky-700" />
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center space-y-4">
            <p className="text-red-600">{error}</p>
            <Button onClick={() => navigate(carsHome)}>
              <Home className="w-4 h-4 mr-2" />
              {t('carRental.backToSearch', 'Araç kiralamaya dön')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const resNo = booking.broker?.reservation_number;
  const total = booking.pricing?.total;
  const currency = booking.pricing?.currency || 'TRY';

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4">
      <Card className="max-w-lg mx-auto">
        <CardContent className="pt-8 pb-8 space-y-5 text-center">
          {brokerPending ? (
            <AlertTriangle className="w-14 h-14 mx-auto text-amber-500" />
          ) : (
            <CheckCircle className="w-14 h-14 mx-auto text-emerald-600" />
          )}
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              {brokerPending
                ? t('carRental.paymentOkBrokerPending', 'Ödeme alındı — broker kaydı bekleniyor')
                : t('carRental.bookingSuccess')}
            </h1>
            <p className="text-muted-foreground mt-2 text-sm">
              {t('carRental.bookingId', 'Rezervasyon no')}: <strong>{booking.id}</strong>
            </p>
            {resNo && (
              <p className="text-sm mt-1">
                {t('carRental.brokerReservationNo', 'Broker rezervasyon no')}:{' '}
                <strong>{resNo}</strong>
              </p>
            )}
            {brokerError && (
              <p className="text-amber-700 text-sm mt-2">
                {decodeURIComponent(brokerError)}
              </p>
            )}
          </div>

          <div className="text-left bg-slate-50 rounded-lg p-4 space-y-2 text-sm">
            <p className="flex items-center gap-2 font-medium">
              <Car className="w-4 h-4" />
              {booking.vehicle?.vehicleName}
            </p>
            <p>
              {booking.search?.pickupLocationName} → {booking.search?.returnLocationName}
            </p>
            <p>
              {booking.search?.pickupDate} {booking.search?.pickupTime} —{' '}
              {booking.search?.returnDate} {booking.search?.returnTime}
            </p>
            <p className="font-semibold text-sky-800">
              {formatPrice(Number(total || 0), currency)}
            </p>
          </div>

          <Button className="bg-sky-700 hover:bg-sky-800" onClick={() => navigate(carsHome)}>
            <Home className="w-4 h-4 mr-2" />
            {t('carRental.backToSearch', 'Araç kiralamaya dön')}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
